import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { marketsApi, marketDaysApi, salesRecordsApi } from '../../services/api';
import AdminPageHeader from '../../components/AdminPageHeader';
import './MarketDays.css';

const WEATHER = ['Sunny', 'Partly Cloudy', 'Cloudy', 'Rainy', 'Cold', 'Hot', 'Windy'];

const EMPTY_FORM = {
  market_id: '',
  date: new Date().toISOString().split('T')[0],
  weather_condition: 'Sunny',
  temperature: '',
  notes: '',
};

export default function MarketDays() {
  const [marketDays, setMarketDays] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [selectedPrintDate, setSelectedPrintDate] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [mdData, mData] = await Promise.all([
        marketDaysApi.getAll(),
        marketsApi.getAll(),
      ]);
      const sorted = (mdData.market_days || []).sort((a, b) => new Date(b.date) - new Date(a.date));
      setMarketDays(sorted);
      setMarkets((mData.markets || []).filter(m => m.is_active));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, location.state?.refresh]);

  useEffect(() => {
    const handleFocus = () => {
      if (sessionStorage.getItem('marketDaysNeedsRefresh') === 'true') {
        sessionStorage.removeItem('marketDaysNeedsRefresh');
        load();
      }
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) handleFocus();
    });
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [load]);

  useEffect(() => {
    if (markets.length > 0 && !form.market_id) {
      setForm(f => ({ ...f, market_id: markets[0].id }));
    }
  }, [markets, form.market_id]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const result = await marketDaysApi.create(form);
      await load();
      setShowForm(false);
      navigate(`/ops/market-days/${result.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handlePrintAllSheets() {
    if (!selectedPrintDate) {
      alert('Please select a date first.');
      return;
    }
    setPrinting(true);
    try {
      const getDateStr = (date) => {
        if (!date) return '';
        const raw = String(date);
        if (raw.length >= 10 && !raw.includes('T')) return raw.slice(0, 10);
        // ISO timestamp - use UTC date to avoid timezone shift
        return raw.slice(0, 10);
      };
      const daysToPrint = marketDays.filter(md => getDateStr(md.date) === selectedPrintDate);

      const allSections = await Promise.all(daysToPrint.map(async md => {
        const market = markets.find(m => String(m.id) === String(md.market_id));
        const recData = await salesRecordsApi.getForMarketDay(md.id);
        const records = recData.records || [];
        const arRows = records.filter(r => r.company === 'AR');
        const ccRows = records.filter(r => r.company === 'CC');

        const AR_CATS = [
          { key: 'ar_frozen', label: 'Frozen & Prepared Foods' },
          { key: 'ar_chicken', label: 'Chicken' },
          { key: 'ar_pork', label: 'Pork' },
          { key: 'ar_refrigerated', label: 'Refrigerated' },
        ];
        const CC_CATS = [
          { key: 'cc_veg', label: 'Fermented Veg' },
          { key: 'cc_brine', label: 'Brines' },
          { key: 'cc_bev', label: 'Kombucha & Beverages' },
          { key: 'cc_keg', label: 'Kegs' },
        ];

        const catRows = (rows, cats) => cats.map(cat => {
          const cr = rows.filter(r => r.category === cat.key);
          if (cr.length === 0) return '';
          return `<tr><td colspan="5" style="background:#e8e8e8;font-weight:bold;padding:6px 8px;">${cat.label}</td></tr>` +
            cr.map(r => `<tr>
              <td>${r.product_name || ''}</td>
              <td style="min-width:60px">${r.qty_packed || ''}</td>
              <td style="min-width:60px"></td>
              <td style="min-width:60px"></td>
              <td style="min-width:80px"></td>
            </tr>`).join('');
        }).join('');

        const dateStr = new Date(md.date).toLocaleDateString('en-US', {weekday:'long', month:'long', day:'numeric', year:'numeric'});

        return `
          ${arRows.length > 0 ? `
          <div style="page-break-after: ${ccRows.length > 0 ? 'always' : 'avoid'};">
            <h2>${market?.name || md.market_id} — Apple Ridge Farm</h2>
            <p>${dateStr} &nbsp;|&nbsp; Weather: _________________ &nbsp; Temp: ______°F &nbsp; Staff: _________________</p>
            <table border="1" cellpadding="6" cellspacing="0" width="100%" style="border-collapse:collapse;font-size:11px">
              <thead><tr style="background:#2c3e50;color:white"><th>Product</th><th>Start</th><th>Finish</th><th>Request Next Week</th><th>Notes</th></tr></thead>
              <tbody>${catRows(arRows, AR_CATS)}</tbody>
            </table>
          </div>` : ''}
          ${ccRows.length > 0 ? `
          <div style="page-break-after: always;">
            <h2>${market?.name || md.market_id} — Collective Culture</h2>
            <p>${dateStr} &nbsp;|&nbsp; Weather: _________________ &nbsp; Temp: ______°F &nbsp; Staff: _________________</p>
            <table border="1" cellpadding="6" cellspacing="0" width="100%" style="border-collapse:collapse;font-size:11px">
              <thead><tr style="background:#2c3e50;color:white"><th>Product</th><th>Start</th><th>Finish</th><th>Request Next Week</th><th>Notes</th></tr></thead>
              <tbody>${catRows(ccRows, CC_CATS)}</tbody>
            </table>
          </div>` : ''}`;
      }));

      const html = `<html><head><title>Market Sheets - ${selectedPrintDate}</title>
        <style>body{font-family:Arial,sans-serif;font-size:12px;margin:20px} h2{margin-bottom:4px} h3{margin:16px 0 6px}</style>
        </head><body>${allSections.join('')}</body></html>`;

      const win = window.open('', '_blank');
      if (!win) { alert('Please allow popups for this site to print.'); setPrinting(false); return; }
      win.document.write(html);
      win.document.close();
      setTimeout(() => win.print(), 500);
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setPrinting(false);
    }
  }

  function getMarketName(id) {
    return markets.find(m => String(m.id) === String(id))?.name || id;
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  }

  return (
    <div className="page">
      <AdminPageHeader
        title="Market Days"
        subtitle={`${marketDays.length} recorded market days`}
        action={
          <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
            <select
              value={selectedPrintDate}
              onChange={e => setSelectedPrintDate(e.target.value)}
              className="market-select"
              style={{minWidth:'160px'}}
            >
              <option value="">Select date to print...</option>
              {[...new Set(marketDays.map(md => md.date ? String(md.date).slice(0,10) : '').filter(Boolean))].sort().reverse().map(date => (
                <option key={date} value={date}>{date}</option>
              ))}
            </select>
            <button className="btn-secondary" onClick={handlePrintAllSheets} disabled={printing || !selectedPrintDate}>
              {printing ? 'Loading...' : '🖨 Print All Sheets'}
            </button>
            <button className="btn-primary" onClick={() => setShowForm(true)}>+ New Market Day</button>
          </div>
        }
      />
      <div className="page-body">
        {error && <div className="error-banner">{error}</div>}

        {loading ? (
          <div className="loading-state">Loading market days...</div>
        ) : marketDays.length === 0 ? (
          <div className="empty-state">No market days yet. Create your first one to get started.</div>
        ) : (
          <div className="markets-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Market</th>
                  <th>Weather</th>
                  <th>Temp</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {marketDays.map(md => (
                  <tr key={md.id}>
                    <td className="td-name">{formatDate(md.date)}</td>
                    <td>{getMarketName(md.market_id)}</td>
                    <td>{md.weather_condition}</td>
                    <td>{md.temperature ? `${md.temperature}°F` : '—'}</td>
                    <td>
                      <span className={`status-pill ${md.status === 'complete' ? 'pill-green' : 'pill-orange'}`}>
                        {md.status === 'complete' ? 'Complete' : 'In Progress'}
                      </span>
                    </td>
                    <td>
                      <button className="btn-edit" onClick={() => navigate(`/ops/market-days/${md.id}`)}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New Market Day</h2>
              <button className="modal-close" onClick={() => setShowForm(false)}>x</button>
            </div>
            <form onSubmit={handleCreate} className="modal-form">
              <div className="field">
                <label>Market *</label>
                <select name="market_id" value={form.market_id} onChange={handleChange} required>
                  {markets.map(m => (
                    <option key={m.id} value={m.id}>{m.name} — {m.day_of_week}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Date *</label>
                <input type="date" name="date" value={form.date} onChange={handleChange} required />
              </div>
              <div className="field-row">
                <div className="field">
                  <label>Weather</label>
                  <select name="weather_condition" value={form.weather_condition} onChange={handleChange}>
                    {WEATHER.map(w => <option key={w} value={w}>{w}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Temperature (°F)</label>
                  <input type="number" name="temperature" value={form.temperature} onChange={handleChange} placeholder="72" />
                </div>
              </div>
              <div className="field">
                <label>Notes</label>
                <input name="notes" value={form.notes} onChange={handleChange} placeholder="Any notes about this market day..." />
              </div>
              {error && <div className="form-error">{error}</div>}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create & Enter Data'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
